import React, { useMemo, useState } from "react";
import { Modal, Form, Input, Typography, Alert } from "antd";

import type { JsonValue } from "../types";

interface Props {
  visible: boolean;
  onClose: () => void;
  onCreate: (params: { fileName: string; seedJson: JsonValue }) => Promise<void>;
}

const FileCreateModal: React.FC<Props> = ({ visible, onClose, onCreate }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const initialJsonText = useMemo(() => "{\n  \"enabled\": true\n}", []);

  const handleSubmit = async () => {
    try {
      setErrorMessage("");
      const values = await form.validateFields();
      const parsedJson = JSON.parse(values.seedJson) as JsonValue;
      setSubmitting(true);
      await onCreate({ fileName: values.fileName, seedJson: parsedJson });
      form.resetFields();
      onClose();
    } catch (error) {
      if (error instanceof SyntaxError) {
        setErrorMessage("Seed JSON is not valid JSON");
      } else if (error instanceof Error && error.message) {
        setErrorMessage(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Add Config File"
      visible={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Create File"
      confirmLoading={submitting}
      width={800}
      destroyOnClose
    >
      {errorMessage ? <Alert type="error" showIcon message={errorMessage} style={{ marginBottom: 12 }} /> : null}
      <Typography.Paragraph>
        Add a new global file. File names are immutable after creation.
      </Typography.Paragraph>
      <Form form={form} layout="vertical" initialValues={{ seedJson: initialJsonText }}>
        <Form.Item
          name="fileName"
          label="File Name"
          rules={[{ required: true, message: "File name is required" }]}
        >
          <Input placeholder="example: dosha-rules.json" />
        </Form.Item>
        <Form.Item
          name="seedJson"
          label="Good JSON (schema source)"
          rules={[{ required: true, message: "Seed JSON is required" }]}
        >
          <Input.TextArea rows={12} spellCheck={false} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FileCreateModal;
